import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {
    attachDirectionHint,
    applySplitDirectionAxes,
    createBoundedAxisTooltip,
    finalizeSplitDirectionChart,
    formatPercentFromHundredthsAxis,
    formatTimeTick,
} from "../../helpers/chart_common";

export class ShaperCake extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
        this.metric = "drops";
        this.metricButtons = null;
    }

    title() {
        return "Shaper CAKE Activity";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Shaper CAKE Activity</h5><p>CAKE interventions to enhance your network.</p>";
    }

    buildContainer() {
        let base = super.buildContainer();
        const controlsWrap = document.createElement("span");
        controlsWrap.classList.add("lqos-cake-toggle");
        const controls = document.createElement("div");
        controls.classList.add("btn-group", "btn-group-sm");

        const dropsBtn = document.createElement("button");
        dropsBtn.type = "button";
        dropsBtn.className = "btn btn-outline-secondary active";
        dropsBtn.innerText = "Drops";

        const marksBtn = document.createElement("button");
        marksBtn.type = "button";
        marksBtn.className = "btn btn-outline-secondary";
        marksBtn.innerText = "Marks";

        dropsBtn.onclick = () => this.setMetric("drops");
        marksBtn.onclick = () => this.setMetric("marks");

        this.metricButtons = { drops: dropsBtn, marks: marksBtn };

        controls.appendChild(dropsBtn);
        controls.appendChild(marksBtn);
        controlsWrap.appendChild(controls);
        const title = base.querySelector(".dashbox-title");
        const titleAction = title?.querySelector("span");
        if (title) {
            title.insertBefore(controlsWrap, titleAction || null);
        }
        attachDirectionHint(base);
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperCakeGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState, this.metric);
        }
    }

    channelRequests() {
        return [ "ShaperDetail" ];
    }

    onMessage(msg) {
        if (msg.Data.target !== "shaper_detail") return;

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        for (const [key, value] of Object.entries(msg.Data.blob)) {
            this.graph.update(value, this.metric);
            this.lastGraphState = value;
        }
    }

    setMetric(metric) {
        if (this.metric === metric) return;
        this.metric = metric;
        if (this.metricButtons) {
            Object.entries(this.metricButtons).forEach(([key, btn]) => {
                if (key === metric) {
                    btn.classList.add("active");
                } else {
                    btn.classList.remove("active");
                }
            });
        }
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState, this.metric);
        }
    }

}

class ShaperCakeGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withScaledAbsYAxis("Marks/Drops", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        this.option && this.chart.setOption(this.option);
    }

    update(data, metric) {
        this.chart.hideLoading();

        const metricLabel = metric === "drops" ? "Drops" : "Marks";
        const color = metric === "drops" ? window.graphPalette[1] : window.graphPalette[0];
        const ticks = data?.body?.ticks || [];
        const downSeries = metric === "drops"
            ? (data?.body?.cake_drops_down_percent || [])
            : (data?.body?.cake_marks_down_percent || []);
        const upSeries = metric === "drops"
            ? (data?.body?.cake_drops_up_percent || [])
            : (data?.body?.cake_marks_up_percent || []);

        applySplitDirectionAxes(this.option, {
            ticks,
            topLabel: "Download",
            bottomLabel: "Upload",
            yFormatter: formatPercentFromHundredthsAxis,
        });

        this.option.series = [
            {
                name: `${metricLabel} Download`,
                type: "line",
                data: downSeries,
                itemStyle: { color },
                lineStyle: { width: 2 },
                smooth: true,
                showSymbol: false,
                xAxisIndex: 0,
                yAxisIndex: 0,
            },
            {
                name: `${metricLabel} Upload`,
                type: "line",
                data: upSeries.map((value) => 0 - value),
                itemStyle: { color },
                lineStyle: { width: 2 },
                smooth: true,
                showSymbol: false,
                xAxisIndex: 1,
                yAxisIndex: 0,
            },
        ];
        this.option.legend = { show: false };

        this.option.tooltip = createBoundedAxisTooltip({
            linkAllXAxes: true,
            yFormatter: formatPercentFromHundredthsAxis,
            formatter: (params) => {
                if (!params || params.length === 0) return "";
                const downPoint = params.find((p) => p.seriesName && p.seriesName.includes("Download"));
                const upPoint = params.find((p) => p.seriesName && p.seriesName.includes("Upload"));
                return `<div style="font-size: 10px;">
${formatTimeTick(params[0].axisValue)}<br/>
<strong>Download ${metricLabel}</strong>: ${downPoint ? formatPercentFromHundredthsAxis(downPoint.value, 2) : "-"}<br/>
<strong>Upload ${metricLabel}</strong>: ${upPoint ? formatPercentFromHundredthsAxis(upPoint.value, 2) : "-"}
</div>`;
            },
        });

        // Add axis pointer configuration for highlighting
        this.option.axisPointer = {
            link: {xAxisIndex: 'all'},
            label: {
                backgroundColor: 'rgba(30,30,30,0.92)',
                color: '#fff',
                fontFamily: 'inherit'
            }
        };

        finalizeSplitDirectionChart(this.option);
        this.chart.setOption(this.option, true);
    }
}
